import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Ban } from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

type Sub = { status: string; grace_days?: number | null } | null;

/** Site-wide subscription banner. Renders only for suspended/past_due tenants.
 *  Refreshes when the active tenant changes so switching support-scope updates the banner. */
export function SubscriptionBanner() {
    const { t } = useTranslation();
    const { activeTenantId, me } = useAuth();
    const [sub, setSub] = useState<Sub>(null);

    useEffect(() => {
        // Platform owners in support scope don't have a tenant membership → skip fetch.
        if (!activeTenantId || me?.user?.is_platform_owner) { setSub(null); return; }
        let mounted = true;
        api.get("/institution/subscription")
            .then((r) => { if (mounted) setSub(r.data?.subscription ?? null); })
            .catch(() => { if (mounted) setSub(null); });
        return () => { mounted = false; };
    }, [activeTenantId, me?.user?.is_platform_owner]);

    if (!sub) return null;
    if (sub.status === "suspended") {
        return (
            <div
                data-testid="sub-banner-suspended"
                role="alert"
                className="w-full flex items-center gap-3 px-4 md:px-6 py-2.5 text-sm"
                style={{ background: "var(--error-bg)", color: "var(--error-fg)", borderBottom: "1px solid var(--border)" }}
            >
                <Ban size={18} aria-hidden="true" />
                <span className="font-medium">{t("sub.banner.suspended")}</span>
            </div>
        );
    }
    if (sub.status === "past_due") {
        const days = typeof sub.grace_days === "number" ? sub.grace_days : 0;
        return (
            <div
                data-testid="sub-banner-past-due"
                role="alert"
                className="w-full flex items-center gap-3 px-4 md:px-6 py-2.5 text-sm"
                style={{ background: "var(--warning-bg)", color: "var(--warning-fg)", borderBottom: "1px solid var(--border)" }}
            >
                <AlertTriangle size={18} aria-hidden="true" />
                <span className="font-medium">{t("sub.banner.past_due", { days })}</span>
            </div>
        );
    }
    return null;
}
